// eslint-disable-next-line import/named
import CustomCollaborateEmpty from 'assets/images/custom_collaborate.svg'
import { generateHSLString } from 'utils/helper'
import { LoadingAnimation } from '@outbound-ai/ursa'
import { IComments } from 'features/claims/types'
import { useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Avatar, Button, Tooltip } from '@outbound-ai/ursa'
import styled from 'styled-components'

import { PositionEnum } from 'types/cssTypes'
import { CommentInput } from './CommentInput'

interface IProps {
  charLimit?: number
  comments: IComments[]
  totalCount: number
  hasMore: boolean
  loading: boolean
  isSaveCompleted: boolean
  disabled?: boolean
  onScroll: () => void
  onSubmit: (text: string) => Promise<void>
}

export const Comments = ({
  comments,
  charLimit = 500,
  hasMore,
  totalCount,
  loading,
  onScroll,
  onSubmit,
  disabled,
  isSaveCompleted,
}: IProps) => {
  const format = new Intl.DateTimeFormat('en-US', { dateStyle: 'full', timeStyle: 'long' })

  const [showInputBox, setInputBox] = useState(false)
  const [showMore, setShowMore] = useState(false)
  const [selectedComment, setSelectedComment] = useState('')
  // const [isSaveCompleted, setSaveComplete] = useState(true)

  const handleSaveComment = (comment: string) => {
    onSubmit(comment)
  }

  const showMoreLess = (noteId: string) => {
    setShowMore(!showMore)
    setSelectedComment(noteId)
  }

  return (
    <Div showInputBox={showInputBox}>
      <CommentInput
        loading={loading}
        showInputBox={showInputBox}
        saveComment={(comment: string) => handleSaveComment(comment)}
        setInputBox={(i) => setInputBox(i)}
        charLimit={charLimit}
        isSaveCompleted={isSaveCompleted}
        disabled={disabled}
      />

      <div className="comments-list-container">
        {/**
         * TODO: the height of the scrollbar has to be changed.
         */}
        <InfiniteScroll
          dataLength={comments.length}
          next={() => onScroll()}
          hasMore={hasMore}
          loader={
            <div className="loader" key={0}>
              <LoadingAnimation /> Loading ...
            </div>
          }
          height={showInputBox ? '14rem' : '20rem'}
          endMessage={
            totalCount <= 0 ? (
              <div className="text-center no-comments-container">
                <img src={CustomCollaborateEmpty} alt="No comments Yet" />
                <h6> No Comments Yet </h6>
                <p>Begin the conversation by clicking above to Add New.</p>
              </div>
            ) : (
              <p className="text-center"> ---- End of comments ---- </p>
            )
          }
        >
          {comments.map((c) => (
            <div className="comment-list-inner-container" key={c.noteId}>
              <div className="avatar-container">
                <div className="comment-dp">
                  <Tooltip
                    tooltipText={`${c.noteCreatorUser.firstName} ${c.noteCreatorUser.lastName}`}
                    position={PositionEnum.bottom}
                    positionTooltipArrow={10}
                    tooltipStyles={{
                      transform: 'translateY(0%) translateX(-7%)',
                    }}
                  >
                    {c.noteCreatorUser.firstName && c.noteCreatorUser.lastName ? (
                      <Avatar
                        initials={
                          c.noteCreatorUser.firstName.charAt(0).toUpperCase() +
                          c.noteCreatorUser.lastName.charAt(0).toUpperCase()
                        }
                        size="medium"
                        backgroundColor={generateHSLString(c.noteCreatorUser.firstName + c.noteCreatorUser.lastName)}
                        color="#32325d"
                        key={c.noteId}
                      />
                    ) : (
                      <Avatar initials="" size="medium" backgroundColor="#FFF" key={c.noteId} />
                    )}
                  </Tooltip>
                </div>
                <div className="avatar-continuation" style={{ paddingTop: c.note.length < 30 ? '10px' : '0' }}>
                  <hr className="avatar-cont-partial-height" />
                </div>
              </div>
              <div className="comment-list-inner">
                <div className="inner-comment">
                  <span className="user-name">{c.noteCreatorUser.firstName} :</span>
                  <p>
                    {showMore && selectedComment == c.noteId ? c.note : `${c.note.substring(0, 50)}`}
                    {c.note.length > 50 ? (
                      <Button
                        variant="tertiary"
                        className="btn show-more-btn"
                        key={c.noteId}
                        onClick={() => showMoreLess(c.noteId)}
                      >
                        {showMore && selectedComment == c.noteId ? 'Show less' : 'Show more'}
                      </Button>
                    ) : (
                      ''
                    )}
                  </p>
                </div>
                <span className="time-stamp"> {format.format(new Date(c.created))}</span>
              </div>
            </div>
          ))}
        </InfiniteScroll>
      </div>
    </Div>
  )
}

const Div = styled.div<{ showInputBox: boolean }>`
  position: relative;
  padding-right: 0.625rem;
  padding-top: 0.3125rem;
  height: 100%;
  .text-area-container {
    position: relative;
    margin-bottom: 0.0625rem;
  }
  textarea:focus,
  input:focus,
  button:focus {
    outline: none;
  }

  .comments-list-container {
    overflow: auto;
    height: ${({ showInputBox }) => (showInputBox ? '14rem' : '23rem')};
    margin-top: 1rem;
  }
  .comment-list-inner-container {
    display: flex;
    padding-bottom: 0.625rem;
    font-size: 0.875rem;
    color: #a3a1a1;
    .comment-dp {
      width: 2.1875rem;
      height: 2.1875rem;
      background: #eceaea;
      color: #626262;
      border: 1px solid #39303000;
      border-radius: 50px;
      align-items: center;
      display: flex;
      justify-content: center;
      margin-right: 0.625rem;
    }
  }
  .comment-list-inner {
    .inner-comment {
      display: block;
      .user-name {
        float: left;
        margin-right: 0.3125rem;
        font-family: ${({ theme }) => theme.fontFamily.milliard};
        font-style: normal;
        font-weight: ${({ theme }) => theme.fontWeight.bold};
        font-size: 0.8125rem;
        line-height: 1.0625rem;
        color: #576573;
      }
      p {
        font-family: ${({ theme }) => theme.fontFamily.milliard};
        font-style: normal;
        font-weight: ${({ theme }) => theme.fontWeight.normal};
        font-size: 0.875rem;
        line-height: 1.0625rem;
        color: #576573;
        word-break: break-word;
      }
    }
  }
  .show-more-btn {
    padding: 0;
    color: ${({ theme }) => theme.colors.primary.darker};
    font-size: 0.75rem;
    font-weight: ${({ theme }) => theme.fontWeight.normal};
  }
  .loader {
    display: flex;
    justify-content: center;
  }
  .time-stamp {
    padding-top: 0.25em;
    font-family: ${({ theme }) => theme.fontFamily.roboto};
    font-style: normal;
    font-weight: ${({ theme }) => theme.fontWeight.normal};
    font-size: 0.7188rem;
    line-height: 1.25rem;
    color: #576573;
    opacity: 0.7;
  }

  .no-comments-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    h6 {
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: ${({ theme }) => theme.fontFamily.milliard};
      font-size: 0.875rem;
      font-weight: ${({ theme }) => theme.fontWeight.bold};
      font-stretch: normal;
      font-style: normal;
      line-height: normal;
      letter-spacing: normal;
      text-align: center;
      color: ${({ theme }) => theme.colors.primary.darker};
      margin-top: 0.625rem;
    }
    p {
      font-family: ${({ theme }) => theme.fontFamily.milliard};
      font-size: 0.875rem;
      font-weight: ${({ theme }) => theme.fontWeight.normal};
      font-stretch: normal;
      font-style: normal;
      line-height: 1.34;
      letter-spacing: normal;
      text-align: center;
      color: ${({ theme }) => theme.colors.grey.neutral};
    }
  }
  .avatar-container {
    position: relative;
    display: grid;
    align-items: stretch;
    justify-items: stretch;
    align-content: stretch;
  }
  .avatar-continuation {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-right: 0.625rem;
  }
  .avatar-cont-partial-height {
    width: 0.0625rem;
    height: 100%;
  }
`
